import * as React from 'react';
import useBreakpoints from '@toolkit/util/useBreakpoints';

// Utils
import {
  formatCents,
  head,
  isNilOrEmpty,
  ifElse
} from '@src/shared/src/util/general';
// Constants
import { ENVIRONMENT } from '@src/shared/src/const/app';
// Actions
// Models
import {
  TariffModel,
  OptionModel,
} from '@src/shared/src/models';
// Interfaces
// Components
import { Spinner } from '@toolkit/ui';
import { CustomSelect } from '@toolkit/ui/CustomSelect';
// Styles
import '../styles/TariffOptions.scss';
import { Trans } from 'react-i18next';

type Props = {
  env: ENVIRONMENT;
  tariffs: TariffModel[];
  selectedTariff: TariffModel;
  changeable?: boolean;
  onChangeTariff: (tariffId: string) => void;
};

const getTariffPriceDifference = (priceDifference: number) =>
  priceDifference < 0 ? formatCents(priceDifference) : `+${formatCents(priceDifference)}`;

const TariffOptions: React.FC<Props> = ({
  env,
  tariffs,
  selectedTariff,
  changeable,
  onChangeTariff,
}) => {
  const { isMd } = useBreakpoints();

  const handleOptionChange = (option: OptionModel) =>
    Number(option.value) !== selectedTariff.id
      ? onChangeTariff(option.value)
      : null;

  const getTariffLabel = (tariff: TariffModel) =>
    `${getTariffPriceDifference(tariff.priceDifference)} - ${tariff.fareName}`;

  const getTariffMarkup = (tariffsList: TariffModel[]) =>
    changeable ? (
      <div className='lp-tariff-options-price'>
        <strong>{formatCents(head(tariffsList).priceDifference)}</strong>
        <div>
          <Trans i18nKey="tariffOptions.label.estimatedPrice">
            <br />
          </Trans>
        </div>
      </div>
    ) : (
      <div className="lp-tariff-options">
        <CustomSelect
          menuPlacement={isMd ? 'top' : 'auto'}
          options={tariffsList.map((tariff) => ({
            label: getTariffLabel(tariff),
            value: String(tariff.id),
          }))}
          onChange={handleOptionChange}
          selectedValue={String(selectedTariff.id)}
          isDisabled={env !== ENVIRONMENT.SEARCH}
        />
      </div>
    );

  const getTariffMarkupLoader = () => <Spinner />;

  const renderTariffs = ifElse(
    isNilOrEmpty,
    getTariffMarkupLoader,
    getTariffMarkup
  );

  return renderTariffs(tariffs);
};

export default TariffOptions;
